import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, map, tap } from 'rxjs';

import { OFFER_SERVER_URL } from '../../config';
import { DataWithTranslation, TranslationType } from '../../shared/models/data-with-translations';
import { ProductA2c, ProductFamily, ProductTable, Feasibility, ProductRef, TemplateDescription,
	BocOpt } from '../models/offer';
import { CodeValue } from '../../shared/models/code-value';
import {
	ProductCharacteristicsTranslationService
} from '../../shared/services/product-characteristics-translation.services';
import { AdycoFields } from '../../shared/models/adyco-fields';

const PRODUCT_FAMILY_LIST_URL = `${ OFFER_SERVER_URL }/v3/products:families`;
const PRODUCT_ADYCO_FIELDS_URL_V2 = `${ OFFER_SERVER_URL }/v2/products:adycoFields`;
const FIELD_NAMES_URL_V2 = `${ OFFER_SERVER_URL }/products:fields`;
const CLEAR_ORDER_BOOK_URL_V2 = `${ OFFER_SERVER_URL }/v2/products:clearCache`;
const PRODUCT_FEASIBILITY_URL = `${ OFFER_SERVER_URL }/products:feasibility`;
const PRODUCT_ADYCOTABLE_URL_V2 = `${ OFFER_SERVER_URL }/v2/products:adycoTable`;
const PRODUCTS_DETAILS = `${ OFFER_SERVER_URL }/v3/products`;
const PRODUCT_TEMPLATE_DETAILS = `${ OFFER_SERVER_URL }/v2/template:search`;

@Injectable({ providedIn: 'root' })
export class ProductService {
	soldToChangeEvent: Observable<string>;
	shipToEndUserChangeEvent: Observable<BocOpt>;
	private readonly soldToChangeEventSubject = new Subject<string>();
	private readonly shipToEndUserChangeEventSubject = new Subject<BocOpt>();
	constructor(
		private readonly http: HttpClient,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService
	) {
		this.soldToChangeEvent = this.soldToChangeEventSubject.asObservable();
		this.shipToEndUserChangeEvent = this.shipToEndUserChangeEventSubject.asObservable();
	}

	emitSoldToChangeEvent(data: string) {
		this.soldToChangeEventSubject.next(data);
	}

	emitShipToEndUserChangeEvent(data: BocOpt) {
		this.shipToEndUserChangeEventSubject.next(data);
	}

	getProductFamily(
		soldTo: string,
		periodInMonth?: number,
		translationType?: TranslationType
	) {
		const productfamilyUrl = new URL(PRODUCT_FAMILY_LIST_URL);
		const params = productfamilyUrl.searchParams;
		params.append('soldTo', soldTo);
		params.append('serviceCode', 'EOFFR');
		if (periodInMonth !== undefined) {
			params.append('month', String(periodInMonth));
		}
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.get<ProductFamily[]>(productfamilyUrl.toString());
	}

	getAdycoFields(category: string) {
		const dataUrl = new URL(PRODUCT_ADYCO_FIELDS_URL_V2);
		const params = dataUrl.searchParams;
		if (category) {
			params.append('category', category);
			params.append('serviceCode', 'EOFFR');
		}
		return this.http.get<AdycoFields>(dataUrl.toString());
	}

	clearCacheOrderbook(
		soldTo: string,
		shipTo: string,
		endUser: string
	) {
		const dataUrl = new URL(CLEAR_ORDER_BOOK_URL_V2);
		const params = dataUrl.searchParams;
		params.append('soldTo', soldTo);
		params.append('shipTo', shipTo);
		params.append('endUser', endUser);
		params.append('serviceCode', 'EOFFR');
		return this.http.post<''>(dataUrl.toString(), undefined);
	}

	getFeasibility(
		productRef: ProductRef,
		productRelease: string,
		includePending = true,
		translationType?: TranslationType
	) {
		const productUrl = new URL(PRODUCT_FEASIBILITY_URL);
		const params = productUrl.searchParams;

		if (!productRef.description.productRef) {
			throw new Error('A2C product reference not found');
		}
		params.append('productRef', productRef.description.productRef);
		params.append('productRelease', productRelease);
		params.append('configuration', String(productRef.description.configurationRef !== undefined));
		params.append('includePending', String(includePending));
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.get<DataWithTranslation<Feasibility>>(productUrl.toString())
			.pipe(
				tap(feasibility => {
					this.productCharacteristicsTranslationService.add(feasibility.translations);
				}),
				map(feasibility => feasibility.data)
			);
	}

	loadFieldNameTranslations(
		a2c: boolean
	) {
		const url = new URL(FIELD_NAMES_URL_V2);
		const params = url.searchParams;
		params.append('a2c', String(a2c));
		return this.http.get<CodeValue[]>(url.toString())
			.pipe(
				tap(fieldNameTranslations => {
					this.productCharacteristicsTranslationService.add(fieldNameTranslations);
				}),
				map(() => true)
			);
	}

	getProductTableOrderBookOnly(
		soldTo: string,
		shipTo: string,
		endUser: string,
		productFamily: string,
		periodInMonth?: number,
		pivotFields?: string[],
		onlyFieldNames?: boolean,
		orderBookOnly?: boolean,
		translationType?: TranslationType
	) {
		const dataUrl = new URL(PRODUCT_ADYCOTABLE_URL_V2);
		const params = dataUrl.searchParams;
		params.append('soldTo', soldTo.padStart(10, '0'));
		params.append('shipTo', shipTo.padStart(10, '0'));
		params.append('endUser', endUser.padStart(10, '0'));
		params.append('family', productFamily);
		params.append('serviceCode', 'EOFFR');
		if (periodInMonth !== undefined) {
			params.append('month', String(periodInMonth));
		}
		if (pivotFields) {
			params.append('pivotFields', pivotFields.join(','));
		}
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		if (onlyFieldNames !== undefined) {
			params.append('onlyFieldNames', String(onlyFieldNames));
		}
		if (orderBookOnly !== undefined) {
			params.append('orderBookOnly', String(orderBookOnly));
		}
		return this.http.get<DataWithTranslation<ProductTable>>(dataUrl.toString())
			.pipe(
				tap(productTable => {
					this.productCharacteristicsTranslationService.add(productTable.translations);
				}),
				map(productTable => productTable.data)
			);
	}

	getProductsDetails(subCategory: string, form: string, soldTo: string) {
		const dataUrl = new URL(PRODUCTS_DETAILS);
		const params = dataUrl.searchParams;
		if (soldTo) {
			params.append('soldTo', soldTo);
		}
		if (subCategory) {
			params.append('subCategory', subCategory);
		}
		if (form) {
			params.append('form', form);
		}
		params.append('serviceCode', 'EOFFR');
		return this.http.get<ProductA2c[]>(dataUrl.toString());
	}

	getTemplateDetails(templateNumber: number) {
		const dataUrl = new URL(PRODUCT_TEMPLATE_DETAILS);
		const params = dataUrl.searchParams;
		if (templateNumber) {
			params.append('templateNumber', templateNumber.toString());
		}
		return this.http.get<TemplateDescription>(dataUrl.toString());
	}
}
